// Hub events: what a hub's agent is told, one entry in its Hub inbox. A hub
// event is a wake-up hint, not the truth: D1 is, and the agent reads the
// hub's state through the Curator API. So a hub event carries IDs only, in
// `ref`, never text or a token, and producers send it with no outbox and no
// exactly-once promise. Its eventId is the producer's natural key, so a
// producer that retries sends the same hub event, which the Hub inbox keeps
// once. Two parts build against this one shape: the Exchange's producers,
// which send hub events to each hub's HubAgent Durable Object over the
// HUB_AGENT binding (src/services/hub-agent.ts), and the HubAgent, which
// decodes and keeps them (hub-agent/src/hosted/events.ts). It imports only
// effect and the contract, as the rest of the contract does. See Design >
// Hub agent, "Hosted".

import { Schema } from "effect";
import { HubId } from "./ids.ts";

/** The kinds of hub event, each bound to a playbook. A new kind fails to compile until a playbook is bound to it. */
export const HUB_EVENT_KINDS = ["hub.registered", "toolset.staged", "test-run.finished", "activation", "hub-secret.refused", "listing.changed", "hub.archived", "hub-token.connected", "cadence.daily", "cadence.weekly", "curator.reply", "curator.repair.requested"] as const;
export const HubEventKind = Schema.Literals(HUB_EVENT_KINDS);
export type HubEventKind = typeof HubEventKind.Type;

// One value of `ref`: an ID, such as a commit, a run ID or a time, as an
// integer or as text of at most 128 letters, digits and `._:-`. That keeps a
// sentence, a URL and most tokens out; it can't tell an ID from a token of
// the same charset, such as a hex secret, so a producer must still send only
// IDs.
const RefId = Schema.Union([Schema.Int, Schema.String.check(Schema.isPattern(/^[A-Za-z0-9._:-]{1,128}$/))]);
const Ref = Schema.Record(Schema.String.check(Schema.isPattern(/^[A-Za-z][A-Za-z0-9]{0,31}$/)), RefId);

/**
 * One hub event. `eventId` starts with its kind and a colon, as
 * `toolset.staged:<Hub ID>:<commit>` does; `occurredAt` is milliseconds since
 * the epoch.
 */
export const HubEvent = Schema.Struct({
  eventId: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9._:-]{3,300}$/)),
  hubId: HubId,
  kind: HubEventKind,
  occurredAt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  ref: Ref,
}).check(Schema.makeFilter((e) => e.eventId.startsWith(`${e.kind}:`) || `A hub event's eventId starts with its kind, ${e.kind}:`));
export type HubEvent = typeof HubEvent.Type;

/**
 * Each kind's natural key: the eventId a producer sends. A producer builds
 * its hub event's ID here, so a retry sends the same one. `day` is a UTC date,
 * yyyy-mm-dd; `isoWeek` an ISO week, yyyy-Www. An activation's `createdAt` is
 * its activation_history row's. A hub secret's `secretId` is its WarmHub
 * token's row ID or its API key's key ID, which a key set again replaces, and
 * `refusedAt` the refused_at the check or the EgressGateway wrote, so one
 * refusal has one eventId and a token refused again after it was restored has
 * a new one. An archive's `archivedAt` is the hub record's archived_at. A
 * listing change's `listing` is the state it moved to and `at` the change's
 * time. A weekly Agent cadence names its ISO week; a Curator reply names its
 * Interaction, so a resubmitted reply is the same hub event and is kept once.
 * A curator's request for a repair round names the toolset's commit and
 * `requestedAt`, the request's time, so each request is its own hub event.
 */
export const eventIdOf = {
  hubRegistered: (hubId: HubId) => `hub.registered:${hubId}`,
  toolsetStaged: (hubId: HubId, commit: string) => `toolset.staged:${hubId}:${commit}`,
  testRunFinished: (runId: string) => `test-run.finished:${runId}`,
  activation: (hubId: HubId, commit: string, createdAt: number) => `activation:${hubId}:${commit}:${createdAt}`,
  hubSecretRefused: (hubId: HubId, secretId: string, refusedAt: number) => `hub-secret.refused:${hubId}:${secretId}:${refusedAt}`,
  listingChanged: (hubId: HubId, listing: string, at: number) => `listing.changed:${hubId}:${listing}:${at}`,
  hubArchived: (hubId: HubId, archivedAt: number) => `hub.archived:${hubId}:${archivedAt}`,
  hubTokenConnected: (hubId: HubId, connectedAt: number) => `hub-token.connected:${hubId}:${connectedAt}`,
  cadenceDaily: (hubId: HubId, day: string) => `cadence.daily:${hubId}:${day}`,
  cadenceWeekly: (hubId: string, isoWeek: string) => `cadence.weekly:${hubId}:${isoWeek}`,
  curatorReply: (interactionId: string) => `curator.reply:${interactionId}`,
  repairRequested: (hubId: HubId, commit: string, requestedAt: number) => `curator.repair.requested:${hubId}:${commit}:${requestedAt}`,
} as const;

/**
 * A requeue's eventId: the hub event's own, without an earlier requeue's
 * `-r<unix ms>` suffix, then `-r<at>`. The Hub inbox keeps each eventId once
 * in any status, so a hub event whose agent action failed is only ever run
 * again under a new eventId; stripping the earlier suffix keeps requeues
 * from piling up suffixes. The suffix is inside the eventId charset, and the
 * kind's prefix stays. See Design > Operations, "Requeuing a hub event".
 */
export const requeuedEventId = (eventId: string, at: number) => `${eventId.replace(/-r\d{10,}$/, "")}-r${at}`;

/**
 * What the HubAgent's deliver() answers: the hub event kept, already kept,
 * or refused, with text the hub agent wrote. The Exchange decodes it with
 * this schema, since it crossed from another Worker.
 */
export const DeliverResult = Schema.Union([
  Schema.Struct({ result: Schema.Literals(["accepted", "duplicate"]) }),
  Schema.Struct({ result: Schema.Literal("refused"), detail: Schema.String }),
]);
export type DeliverResult = typeof DeliverResult.Type;
